import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TermsAcceptanceRecord } from '../legal/terms-acceptance.service';

export interface BillingPlan {
  currency: string;          // 'AUD'
  interval: string;          // 'year'
  name: string;
  amount_ex_gst: number;     // 400
  gst: number;               // 40
  amount_inc_gst: number;    // 440
  gst_rate_percent: number;  // 10
}

/** What the organisation has accepted, from /api/billing/status.terms and
 *  /api/legal/acceptance. Lets the panel show the acceptance instead of asking for it
 *  again, and ask again when a new version is published. */
export interface TermsAcceptanceState {
  current_terms_version: string;
  /** Whether the server refuses checkout without an acceptance. */
  required: boolean;
  accepted: boolean;
  /** Accepted, and against the version the server is currently serving. */
  up_to_date: boolean;
  /** How many acceptances have been recorded — the log is append-only. */
  acceptances: number;
  acceptance: {
    id: number;
    version: string;
    accepted_at: string;
    actor: string;
    organisation: string;
    authority: boolean;
    ip: string | null;
    user_agent: string | null;
    source: string | null;
    client_claimed_at: string | null;
  } | null;
}

/** A notice the Terms oblige us to give. Today only price changes (clause 12), which
 *  carry 45 days' notice and appear as a banner until they take effect. */
export interface BillingNotice {
  id: number;
  kind: string;
  period_end: string | null;
  effective_from: string | null;
  old_amount: number | null;
  new_amount: number | null;
  currency: string | null;
  note: string | null;
  created_at: string | null;
  created_by: string | null;
  sent_at: string | null;
  delivery_error: string | null;
}

/** The subscription as the backend reads it, so no client has to decide what
 *  'trialing' or 'past_due' means. `show_subscribe` is the one the button reads. */
export interface SubscriptionState {
  /** Stripe's own words: none | active | trialing | past_due | canceled | … */
  status: string;
  active: boolean;
  /** Entitled to the paid features (includes past_due, which is grace). */
  access: boolean;
  show_subscribe: boolean;
  /** past_due / incomplete / unpaid — ask for a card, but lock nothing. */
  needs_payment_action: boolean;
  period_start: string | null;
  period_end: string | null;
  days_remaining: number | null;
  expired: boolean;
  /** Set to stop at the end of the paid period. Still live until then. */
  cancel_at_period_end: boolean;
  /** Next automatic renewal — null when renewal is off. */
  renews_on: string | null;
  /** When access stops — set only when renewal is off. */
  ends_on: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  trial_end: string | null;
  subscription: {
    id: string | null;
    customer_id: string | null;
    price_id: string | null;
    /** Dollars, excluding GST. */
    amount: number | null;
    currency: string | null;
    interval: string | null;
  };
  last_payment: {
    invoice_id: string | null;
    amount: number | null;
    currency: string | null;
    status: string | null;
    at: string | null;
    next_attempt: string | null;
  };
  updated_at: string | null;
  source: string | null;
}

export interface BillingStatus extends SubscriptionState {
  organisation: string | null;
  plan: BillingPlan;
  /** Whether the server actually withholds features without a subscription. */
  gated: boolean;
  /** False when the server has no STRIPE_SECRET_KEY. The page still renders the
   *  price — it comes from the backend, not from Stripe — so a developer machine
   *  shows something real instead of an error. */
  configured: boolean;
  /** Only an admin may start checkout or change renewal. */
  can_manage: boolean;
  terms: TermsAcceptanceState;
  notices: BillingNotice[];
}

/** One row from GET /api/billing/invoices. Amounts are dollars; dates are ISO.
 *  View/Download open Stripe's own URLs — nothing is generated or stored here. */
export interface BillingInvoice {
  id: string;
  number: string | null;
  description: string | null;
  status: string;
  paid: boolean;
  issued_at: string | null;
  paid_at: string | null;
  period_start: string | null;
  period_end: string | null;
  total: number | null;
  amount_due: number | null;
  amount_paid: number | null;
  currency: string | null;
  /** Stripe hosted invoice page (also where an unpaid invoice can be paid). */
  view_url: string | null;
  /** Direct PDF download from Stripe. */
  pdf_url: string | null;
}

export interface BillingInvoicesResponse {
  invoices: BillingInvoice[];
  configured: boolean;
  /** Explains an empty list — show instead of an empty table. */
  note: string | null;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  getStatus(): Observable<BillingStatus> {
    return this.http.get<BillingStatus>(`${this.baseUrl}/billing/status`);
  }

  /**
   * Invoices and receipts from Stripe, newest first. Separate from getStatus() so a
   * Stripe outage does not take down the whole billing screen. Admins only.
   * Open view_url / pdf_url in a new tab — do not fetch the PDF through our API.
   */
  getInvoices(limit = 24): Observable<BillingInvoicesResponse> {
    return this.http.get<BillingInvoicesResponse>(
      `${this.baseUrl}/billing/invoices?limit=${Math.max(1, Math.min(limit, 100))}`
    );
  }

  /** What this organisation has accepted. Also on /billing/status, so the panel does
   *  not normally need this call — it is here for a page that wants only the
   *  acceptance. */
  getAcceptance(): Observable<TermsAcceptanceState> {
    return this.http.get<TermsAcceptanceState>(`${this.baseUrl}/legal/acceptance`);
  }

  /** Returns a Stripe-hosted URL. Send the browser there — card details are entered
   *  on Stripe's page and never touch our servers. The promotion code box on that
   *  page is where a coupon is entered.
   *
   *  `acceptance` carries the Terms acceptance record (clause 1.5 / Acceptance record
   *  table) so acceptance and the Order it authorises arrive in the same request. The
   *  server records it append-only before Stripe is called, and refuses checkout
   *  without it — a 400 here is that refusal, not a Stripe failure. */
  startCheckout(
    acceptance?: TermsAcceptanceRecord
  ): Observable<{ checkout_url: string; session_id: string }> {
    const body = acceptance ? { terms_acceptance: acceptance } : {};
    return this.http.post<{ checkout_url: string; session_id: string }>(
      `${this.baseUrl}/billing/checkout`, body);
  }

  /** Stop renewing at the end of the paid period (clause 12). Not a refund and not an
   *  immediate cancellation — access continues to `ends_on`. */
  cancelRenewal(): Observable<SubscriptionState> {
    return this.http.post<SubscriptionState>(
      `${this.baseUrl}/billing/cancel-renewal`, {});
  }

  /** Put a subscription that was set to stop back on auto-renewal. */
  resumeRenewal(): Observable<SubscriptionState> {
    return this.http.post<SubscriptionState>(
      `${this.baseUrl}/billing/resume-renewal`, {});
  }

  /** Ask Stripe for the current subscription and store the answer. Needed after
   *  returning from checkout locally, where Stripe cannot reach a laptop to send a
   *  webhook. */
  refresh(): Observable<{ status: string; subscription_id: string | null }> {
    return this.http.post<{ status: string; subscription_id: string | null }>(
      `${this.baseUrl}/billing/refresh`, {});
  }
}
